import {AppointmentRequestArchiving} from '@components/appointment-details/appointment-request-archiving';
import {
  AppointmentRequestGroupAppointment
} from '@components/appointment-request-details/appointment-request-group-appointment';
import {Maybe} from '@graphql-tools/utils/typings/types';
import {Card, Empty, Space} from 'antd';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {buildTimestampDateTime} from '../../helpers/formatters';
import {useCurrentUser} from '../../helpers/utils';
import {Resource_AppointmentRequestStatus, useGetAppointmentRequestQuery,} from '@/generated/graphql';
import styles from '../appointment-details/appointment-details.module.scss';
import {FacilityDetails} from '../appointment-details/facility-details';
import {AveliosButton} from '../common-layout/avelios-button';
import {AveliosSpinner} from '../loading/avelios-spinner';
import {UserDetails} from '../user/user-details';
import {AppointmentRequestAppointments} from './appointment-request-appointments';
import {AppointmentRequestHeader} from './appointment-request-header';
import {AppointmentRequestProposals} from './appointment-request-proposals';
import {AppointmentRequestStatus} from './appointment-request-status';
import {CancelAppointmentRequestModal} from './cancel-appointment-request-modal';
import {RequestAddition} from './request-addition';

interface AppointmentDetailsCardProps {
  clinicId: Maybe<string> | undefined;
  appointmentRequestId: Maybe<string> | undefined;
}

export const AppointmentRequestDetails: FC<AppointmentDetailsCardProps> = ({
  appointmentRequestId,
  clinicId,
}) => {
  const { formatMessage } = useIntl();

  const { currentUser } = useCurrentUser();

  const { data: appointmentRequestData, loading } = useGetAppointmentRequestQuery({
    fetchPolicy: 'network-only',
    skip: !clinicId || !appointmentRequestId,
    variables: {
      appointmentRequestId,
      clinicId: clinicId!,
    },
  });
  const appointmentRequestProxy = appointmentRequestData?.getAppointmentRequest;

  const appointmentRequest = appointmentRequestProxy?.appointmentRequest;
  const inProposal =
    appointmentRequest?.status ===
    Resource_AppointmentRequestStatus.AppointmentRequestStatusInProposal;

  const submitted =
    appointmentRequest?.status ===
    Resource_AppointmentRequestStatus.AppointmentRequestStatusSubmitted;

  const portalFacilities = appointmentRequestProxy?.portalFacilityPath?.portalFacilities;
  const officeHour = portalFacilities?.[portalFacilities?.length - 1];

  const appointmentRequestStatus = appointmentRequest?.status;
  const note = appointmentRequest?.comment;

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

  const showCancel =
    appointmentRequestStatus !==
      Resource_AppointmentRequestStatus.AppointmentRequestStatusCancelled &&
    appointmentRequestStatus !==
      Resource_AppointmentRequestStatus.AppointmentRequestStatusFulfilled &&
    appointmentRequestStatus !==
      Resource_AppointmentRequestStatus.AppointmentRequestStatusProposalRejected &&
    appointmentRequestStatus !== Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected;

  return (
    <AveliosSpinner spinning={loading}>
      {appointmentRequestProxy ? (
        <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
          <AppointmentRequestHeader appointmentRequestProxy={appointmentRequestProxy} />
          {appointmentRequest?.appointmentType?.groupAppointment && (
            <AppointmentRequestGroupAppointment
              appointmentRequest={appointmentRequest}
              appointments={appointmentRequest?.appointments}
            />
          )}
          {note && note.length > 0 && <RequestAddition addition={note} />}
          {inProposal && (
            <AppointmentRequestProposals appointmentRequestProxy={appointmentRequestProxy} />
          )}
          {!inProposal && !submitted && (
            <AppointmentRequestAppointments
              clinicId={appointmentRequestData?.getAppointmentRequest?.clinicId}
              appointments={appointmentRequest?.appointments}
            />
          )}
          <AppointmentRequestArchiving appointmentRequest={appointmentRequest} />
          <AppointmentRequestStatus appointmentRequestProxy={appointmentRequestProxy} />
          <Card>
            <FacilityDetails portalFacility={officeHour} />
          </Card>
          <Card>
            <UserDetails userOverride={currentUser ?? undefined} reduced />
          </Card>
          {showCancel && (
            <Card>
              <div>
                <AveliosButton block danger onClick={() => setCancelModalOpen(true)}>
                  {formatMessage({ id: 'appointment-request-cancel' })}
                </AveliosButton>
              </div>
              {appointmentRequest?.submissionDate && (
                <p
                  style={{
                    color: 'darkgray',
                    textAlign: 'center',
                    marginTop: 8,
                    marginBottom: 0,
                  }}
                >
                  {formatMessage(
                    { id: 'appointment-request.booked-at' },
                    {
                      bookedAt: buildTimestampDateTime(appointmentRequest?.submissionDate),
                    },
                  )}
                </p>
              )}
            </Card>
          )}
        </Space>
      ) : (
        <Card style={{ backgroundColor: 'white' }}>
          <div className={styles.empty}>
            <Empty description={formatMessage({ id: 'appointment-details.empty' })} />
          </div>
        </Card>
      )}
      <CancelAppointmentRequestModal
        appointmentRequest={appointmentRequest}
        clinicId={clinicId}
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
      />
    </AveliosSpinner>
  );
};
